import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef } from 'react';

import { Icon } from '@column/column-ui-kit';
import { Badge, DataTable, type DataTableColumnDef } from '@column/ui';

import { ROUTE } from '~/app/routes';
import { Loading } from '~/components';
import { Empty as TableEmpty, getTimestampColumn, tableDefaults, useTimeFormat } from '~/components/Table';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { CardToken, CardTokenRepository } from '~/repositories/CardTokenRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatString } from '~/util';

export const PageCardTokensOverview: React.FC = () => {
  const navigate = useNavigate();
  const { currentPlatform, isSandbox } = useSessionStore();
  const {
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    pageSize,
    reset: resetPagination,
    setPageSize,
  } = usePaginationState();
  const { timeFormat, onTimeFormatChange } = useTimeFormat('card-tokens');

  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [currentPlatform?.id, isSandbox, resetPagination]);

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ['card-tokens', 'list', currentPlatform?.id, isSandbox, pageSize, cursor],
    queryFn: () => CardTokenRepository.listCardTokens({ limit: pageSize, startingAfter: cursor }),
    enabled: Boolean(currentPlatform?.id),
    placeholderData: keepPreviousData,
  });
  const cardTokens = data?.cardTokens ?? [];
  const hasMore = Boolean(data?.hasMore);

  const columns: DataTableColumnDef<CardToken>[] = [
    {
      header: 'Token ID',
      accessor: 'id',
      width: 'minmax(min-content, 1fr)',
      cell: ({ value }) => (
        <DataTable.TextCell displayVariant="monospaced" weight="medium" value={(value as string) || '-'} />
      ),
    },
    {
      header: 'Card ID',
      accessor: 'cardId',
      width: 'minmax(min-content, 1fr)',
      cell: ({ value }) => <DataTable.TextCell displayVariant="monospaced" value={(value as string) || '-'} />,
    },
    {
      header: 'Wallet Type',
      accessor: 'walletType',
      width: 'minmax(min-content, 1fr)',
      cell: ({ value }) => <Badge variant="secondary">{value ? formatString(value as string) : '-'}</Badge>,
    },
    {
      header: 'Token Type',
      accessor: 'tokenType',
      width: 'minmax(min-content, 1fr)',
      cell: ({ value }) => <Badge variant="secondary">{value ? formatString(value as string) : '-'}</Badge>,
    },
    {
      header: 'Device Name',
      accessor: 'deviceName',
      width: 'minmax(min-content, 1fr)',
      cell: ({ value }) => <DataTable.TextCell value={(value as string) || '-'} />,
    },
    {
      header: 'Status',
      accessor: 'status',
      width: 'min-content',
      cell: ({ value }) => <Badge variant="secondary">{value ? formatString(value as string) : '-'}</Badge>,
    },
    getTimestampColumn<CardToken>({ accessor: 'createdAt', timeFormat, onTimeFormatChange, fallback: '-' }),
  ];

  return (
    <>
      <Loading isLoading={isLoading}>
        <Inner pt={0} px="var(--page-margin)">
          <DataTable<CardToken>
            {...tableDefaults}
            columns={columns}
            data={isError ? [] : cardTokens}
            rowKey={(row) => row.id}
            isLoading={isFetching}
            emptyContent={
              isError ? (
                <TableEmpty icon={<Icon.Warning />} title="Failed to load card tokens" />
              ) : (
                <TableEmpty
                  icon={<Icon.AnimationWallet />}
                  title="No card tokens found"
                  description="Card tokens will appear here once cards are added to digital wallets."
                />
              )
            }
            pagination={{
              hasNext: hasMore,
              hasPrevious,
              pageIndex,
              pageSize,
              onNext: () => {
                if (cardTokens.length > 0) {
                  onNext({ cursor: cardTokens[cardTokens.length - 1].id });
                }
              },
              onPrevious,
              onPageSizeChange: setPageSize,
            }}
            onRowClick={(row) => {
              navigate(`${ROUTE.CARD_TOKENS}/edit/${row.id}`);
            }}
          />
        </Inner>
      </Loading>
    </>
  );
};
